import { isErrorFromAlias, Zodios } from "@zodios/core";
import { memoize } from "es-toolkit";

import { toast } from "@skilitics/designer-ui-components";
import { GenerationError } from "@skilitics/interact-ai";
import { TransientError } from "@skilitics/nautilus-core-designer";
import type { NautilusApi } from "@skilitics/nautilus-core-designer/client";
import { api } from "@skilitics/nautilus-endpoint-definition";

import Base64 from "../shared/base64.ts";
import { getRequiredAppSetting } from "../shared/index.ts";
import { attachAuthorizationMiddleware } from "./infrastructure/index.ts";

const endpoint = getRequiredAppSetting(import.meta.env.REACT_APP_NAUTILUS_ENDPOINT, "NAUTILUS_ENDPOINT");
const apiClient = new Zodios(endpoint, api);
attachAuthorizationMiddleware(apiClient.axios);

// NOTE: the memoize of query keys is due to react hook deps needing stable ref
const nautilusApi: NautilusApi = {
  languages: {
    getQueryKey: memoize(() => ["languages"]),
    getAll: apiClient.getLanguages,
  },
  contentLanguages: {
    getQueryKey: memoize((hostId) => ["contentLanguages", hostId]),
    getAll: apiClient.getContentLanguages,
    add: apiClient.createContentLanguage,
    update: apiClient.updateContentLanguage,
    remove: (args) => apiClient.deleteContentLanguage(undefined, args), // zodios wierdness
  },
  nodeDesigns: {
    getQueryKey: memoize((hostId) => ["content", hostId]),
    get: apiClient.getNodeDesigns,
    applyRevision: apiClient.postNodeDesignsRevision,
    undo: ({ params }) => apiClient.patchNodeDesignsRevision({ skip: true }, { params }),
    redo: ({ params }) => apiClient.patchNodeDesignsRevision({ skip: false }, { params }),
  },
  permissions: {
    get: apiClient.getPermissions,
  },
  copilot: {
    getStatus: () => apiClient.getCopilotStatus(),
    getOpenAIApiKey: () => apiClient.getOpenAIApiKey(),
    setOpenAIApiKey: (value) => apiClient.setOpenAIApiKey({ value }),
    getQuestionAnswers: async ({ question, additionalContext }) => {
      try {
        return await apiClient.getQuestionAnswers({ queries: { question, additionalContext } });
      } catch (error) {
        // @ts-ignore
        error.config.method = "get"; // workaround for zodios bug
        if (isErrorFromAlias(api, "getQuestionAnswers", error)) {
          const { response } = error;

          if (response.status === 400) {
            throw new GenerationError(response.data);
          }

          throw new GenerationError({ code: "invalid_request_error", message: response.data, status: response.status });
        }

        throw error;
      }
    },
    getSceneSuggestions: async (interactModule) => {
      try {
        return await apiClient.getSceneSuggestions({ queries: { interactModule } });
      } catch (error) {
        // @ts-ignore
        error.config.method = "get"; // workaround for zodios bug
        if (isErrorFromAlias(api, "getSceneSuggestions", error)) {
          const { response } = error;

          if (response.status === 400) {
            throw new GenerationError(response.data);
          }

          throw new GenerationError({ code: "invalid_request_error", message: response.data, status: response.status });
        }

        throw error;
      }
    },
    getRefinedContent: async (input) => {
      try {
        return await apiClient.getRefinedContent({ queries: { input } });
      } catch (error) {
        // @ts-ignore
        error.config.method = "get"; // workaround for zodios bug
        if (isErrorFromAlias(api, "getRefinedContent", error)) {
          const { response } = error;

          if (response.status === 400) {
            throw new GenerationError(response.data);
          }

          throw new GenerationError({ code: "invalid_request_error", message: response.data, status: response.status });
        }

        throw error;
      }
    },
    getGeneratedImages: async (input) => {
      try {
        return await apiClient.getGeneratedImages({ queries: { input } });
      } catch (error) {
        // @ts-ignore
        error.config.method = "get"; // workaround for zodios bug
        if (isErrorFromAlias(api, "getGeneratedImages", error)) {
          const { response } = error;

          throw new GenerationError({ code: "invalid_request_error", message: response.data, status: response.status });
        }

        throw error;
      }
    },
  },
  nodeTemplates: {
    add: (payload) => apiClient.createNodeTemplate(payload),
    update: (id, payload) => apiClient.updateNodeTemplate(payload, { params: { id } }),
    query: (query) => apiClient.queryNodeTemplates({ queries: query }),
    remove: (id: number) => apiClient.deleteNodeTemplate(undefined, { params: { id } }),
    getCategories: apiClient.getNodeTemplateCategories,
  },
  thumbnails:
    import.meta.env.REACT_APP_DISABLE_THUMBNAILS === "true"
      ? {
          getThumbnailHash: async () => "hi",
          getThumbnail: async () => undefined,
          addThumbnail: async () => {
            //
          },
        }
      : {
          getThumbnailHash: async ({ hostId, nodeId, languageCode }) =>
            await apiClient.getThumbnailHash({ params: { hostId, nodeId }, queries: { languageCode } }),
          getThumbnail: async ({ hostId, nodeId, languageCode }) =>
            await apiClient.getThumbnail({ params: { hostId, nodeId }, queries: { languageCode } }),
          addThumbnail: async ({ file, hostId, nodeId, languageCode }) => {
            const hash = new Uint8Array(await crypto.subtle.digest("SHA-256", await file.arrayBuffer()));
            const { url } = await apiClient.uploadThumbnail({
              params: { hostId, nodeId, hash: Base64.URL_SAFE.encode(hash) },
              queries: { languageCode },
            });

            await fetch(url, {
              method: "PUT",
              body: file,
              headers: {
                "x-amz-checksum-sha256": Base64.STANDARD.encode(hash),
              },
            });
          },
        },
};

const throwModuleLockedError = async (): Promise<any> => {
  const message = "The module is locked.";
  toast.error(message, { duration: 4000 });

  throw new TransientError(message);
};

const readonlyNautilusApi: NautilusApi = {
  ...nautilusApi,
  contentLanguages: {
    ...nautilusApi.contentLanguages,
    add: throwModuleLockedError,
    update: throwModuleLockedError,
    remove: throwModuleLockedError,
  },
  nodeDesigns: {
    ...nautilusApi.nodeDesigns,
    applyRevision: throwModuleLockedError,
  },
  thumbnails: {
    ...nautilusApi.thumbnails,
    addThumbnail: throwModuleLockedError,
  },
};

export const useNautilusApi = (): NautilusApi => {
  return nautilusApi;
};

export const useReadOnlyNautilusApi = (): NautilusApi => {
  return readonlyNautilusApi;
};
